import React from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { HandlingUnitStage, HandlingUnitLocation } from '../../../types/purchaseOrder';
import { EditableHandlingUnit, EditablePurchaseOrderItem } from './types';

interface HandlingUnitsTableProps {
  item: EditablePurchaseOrderItem;
  itemIndex: number;
  newHU: { huNumber?: string };
  onUpdateHandlingUnit: (itemIndex: number, huIndex: number, field: keyof EditableHandlingUnit, value: any) => void;
  onRemoveHandlingUnit: (itemIndex: number, huIndex: number) => void;
  onAddHandlingUnit: (itemIndex: number) => void;
  onNewHUChange: (huNumber: string) => void;
}

/**
 * Readable, labeled handling-unit editor for a single line.
 * Each HU is rendered as a spaced card of labeled fields instead of a cramped
 * nested grid. Duplicate HU numbers are still allowed (the backend suffixes and
 * flags them for review).
 */
const HandlingUnitsTable: React.FC<HandlingUnitsTableProps> = ({
  item,
  itemIndex,
  newHU,
  onUpdateHandlingUnit,
  onRemoveHandlingUnit,
  onAddHandlingUnit,
  onNewHUChange
}) => {
  const stageOptions = Object.values(HandlingUnitStage).map(stage => ({
    label: stage,
    value: stage
  }));

  const locationOptions = Object.values(HandlingUnitLocation).map(location => ({
    label: location,
    value: location
  }));

  const truckOptions = [1, 2].map(num => ({
    label: `Truck ${num}`,
    value: num
  }));

  const handleAddHU = () => {
    if (!newHU.huNumber?.trim()) return;

    // Duplicate HU numbers are allowed here — the backend deterministically
    // suffixes the HU number and flags the PO for review when a duplicate
    // is detected, so we no longer block entry client-side.
    onAddHandlingUnit(itemIndex);
    onNewHUChange('');
  };

  const handlingUnits = item.handlingUnits || [];

  return (
    <div>
      {handlingUnits.length === 0 && (
        <div className="text-sm text-color-secondary mb-2">
          No handling units yet. Add one below.
        </div>
      )}

      {handlingUnits.map((hu, huIndex) => (
        <div
          key={huIndex}
          className="surface-50 border-round p-3 mb-2 border-1 surface-border"
        >
          <div className="flex justify-content-between align-items-center mb-2">
            <span className="font-semibold text-sm">
              Handling Unit {huIndex + 1}
            </span>
            <Button
              icon="pi pi-trash"
              className="p-button-danger p-button-text p-button-sm"
              onClick={() => onRemoveHandlingUnit(itemIndex, huIndex)}
              tooltip="Remove Handling Unit"
              tooltipOptions={{ position: 'top' }}
            />
          </div>

          <div className="formgrid grid">
            <div className="field col-12 md:col-4">
              <label className="text-sm">HU Number</label>
              <InputText
                value={hu.huNumber || ''}
                onChange={(e) => onUpdateHandlingUnit(itemIndex, huIndex, 'huNumber', e.target.value)}
                placeholder="Enter HU Number"
                className="w-full"
              />
            </div>

            <div className="field col-12 md:col-4">
              <label className="text-sm">Stage</label>
              <Dropdown
                value={hu.stage || HandlingUnitStage.Pending}
                options={stageOptions}
                onChange={(e) => onUpdateHandlingUnit(itemIndex, huIndex, 'stage', e.value)}
                placeholder="Select Stage"
                optionLabel="label"
                optionValue="value"
                className="w-full"
              />
            </div>

            <div className="field col-12 md:col-4">
              <label className="text-sm">Location</label>
              <Dropdown
                value={hu.location}
                options={locationOptions}
                onChange={(e) => onUpdateHandlingUnit(itemIndex, huIndex, 'location', e.value)}
                placeholder="Select Location"
                optionLabel="label"
                optionValue="value"
                showClear
                className="w-full"
              />
            </div>

            <div className="field col-12 md:col-4">
              <label className="text-sm">Received Count</label>
              <InputNumber
                value={hu.receivedCount ?? null}
                onValueChange={(e) =>
                  onUpdateHandlingUnit(
                    itemIndex,
                    huIndex,
                    'receivedCount',
                    e.value ?? undefined
                  )
                }
                placeholder="Enter Count"
                min={0}
                showButtons={false}
                className="w-full"
              />
            </div>

            {hu.stage === HandlingUnitStage.Completed && (
              <div className="field col-12 md:col-4">
                <label className="text-sm">Truck Number</label>
                <Dropdown
                  value={hu.handlingUnitOutboundTruckNumber}
                  options={truckOptions}
                  onChange={(e) =>
                    onUpdateHandlingUnit(itemIndex, huIndex, 'handlingUnitOutboundTruckNumber', e.value)
                  }
                  placeholder="Select Truck"
                  optionLabel="label"
                  optionValue="value"
                  showClear
                  className="w-full"
                />
              </div>
            )}

            <div className="field col-12">
              <label className="text-sm">Notes</label>
              <InputTextarea
                value={hu.notes || ''}
                onChange={(e) => onUpdateHandlingUnit(itemIndex, huIndex, 'notes', e.target.value)}
                rows={2}
                placeholder="Add notes..."
                autoResize
                className="w-full"
              />
            </div>
          </div>
        </div>
      ))}

      <div className="flex align-items-center gap-2 mt-2">
        <InputText
          placeholder="New HU Number"
          value={newHU.huNumber || ''}
          onChange={(e) => onNewHUChange(e.target.value.trim())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddHU();
            }
          }}
        />
        <Button
          label="Add HU"
          icon="pi pi-plus"
          onClick={handleAddHU}
          className="p-button-sm p-button-outlined"
          tooltip="Add Handling Unit"
          disabled={!newHU.huNumber?.trim()}
        />
      </div>
    </div>
  );
};

export default HandlingUnitsTable;
